import React from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { FundCalendar } from "@/types";
import FundCalendarDetails from "./components/FundCalendarDetails";
import FundCalendarsList from "./components/FundCalendarsList";
import FundCalendarFormModal from "./components/FundCalendarFormModal";
import { ReportingPeriodTimeline } from "../reporting-periods";
import { useFundCalendarsController } from "./useFundCalendarsController";
import { Badge } from "@/components/ui/badge";
import { useAtomValue } from "jotai";
import { SelectedFundAtom } from "@/store/FundStateAtom";

export default function FundCalendarsView() {
  const controller = useFundCalendarsController();
  const selectedFund = useAtomValue(SelectedFundAtom);

  return (
    <div className="container mx-auto">
      <ReportingPeriodTimeline />
      <div className="flex justify-between items-center mb-6 mt-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Fund Calendars</h1>
          {controller.viewContext === "fund" && selectedFund && (
            <Badge variant="outline" className="text-sm">
              Filtered for {selectedFund.name}
            </Badge>
          )}
          {controller.viewContext === "client" && (
            <Badge variant="outline" className="text-sm">
              All calendars
            </Badge>
          )}
        </div>
        <Button
          onClick={controller.handleCreateNew}
          className="flex items-center gap-2"
        >
          <Plus size={16} />
          New Calendar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <FundCalendarsList
            calendars={controller.fundCalendars}
            selectedCalendar={controller.selectedCalendar}
            onSelect={controller.handleCalendarSelect}
            isLoading={controller.isLoading}
          />
        </div>
        <div className="md:col-span-2">
          {controller.selectedCalendar ? (
            <FundCalendarDetails
              calendar={controller.selectedCalendar}
              onEdit={() =>
                controller.handleEditCalendar(
                  controller.selectedCalendar as FundCalendar
                )
              }
              onRefresh={controller.fetchCalendars}
            />
          ) : (
            <div className="bg-gray-50 border rounded-lg p-8 text-center">
              <h3 className="text-lg font-medium text-gray-500">
                Select a fund calendar or create a new one
              </h3>
              <p className="text-gray-400 mt-2">
                Fund calendars help define the reporting periods for your funds
              </p>
            </div>
          )}
        </div>
      </div>

      <FundCalendarFormModal
        isOpen={controller.isModalOpen}
        onClose={controller.handleModalClose}
        calendar={controller.selectedCalendar}
      />
    </div>
  );
}
